import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import './ManualInput.css';

const API_BASE_URL = 'http://localhost:5001';

export default function ManualInput() {
    const navigate = useNavigate();

    const [availableVehicles, setAvailableVehicles] = useState([]);
    const [availableChargers, setAvailableChargers] = useState([]);
    const [experimentName, setExperimentName] = useState("");
    const [username, setUsername] = useState("");
    const [uid, setUid] = useState("");
    // Each object in these arrays will have a unique 'id' generated by uuidv4
    const [inputVehicles, setInputVehicles] = useState([{ id: uuidv4(), vehicleModel: '', count: '', countFlex: '' }]);
    const [inputChargers, setInputChargers] = useState([{ id: uuidv4(), chargerModel: '', count: '', countFlex: '' }]);
    const [inputRoutes, setInputRoutes] = useState([{ id: uuidv4(), startTime: '', endTime: '', avgSpeed: '', distance: '', numVehicles: '', shiftTime: '' }]);

    const [gridPowerLimit, setGridPowerLimit] = useState('');
    const [peakPrice, setPeakPrice] = useState('0.20'); // Default peak price of $0.20/kWh
    const [energyPrices, setEnergyPrices] = useState(Array(24).fill('').map((_, i) => ({
        hour: i,
        // Default mock prices that follow a typical daily pattern
        // Lower at night (hours 0-5, 22-23), higher during peak hours (7-10, 17-20)
        price: i >= 0 && i <= 5 ? '0.08' :
            i >= 6 && i <= 10 ? '0.15' :
                i >= 11 && i <= 16 ? '0.12' :
                    i >= 17 && i <= 20 ? '0.18' :
                        '0.10' // Hours 21-23
    }))); // Hourly energy prices with default mock values

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submissionStatus, setSubmissionStatus] = useState('');
    const [generatedScenario, setGeneratedScenario] = useState(null);

    useEffect(() => {
        const storedUsername = localStorage.getItem('username');
        const storedUid = localStorage.getItem('uid');
        if (storedUsername) {
            setUsername(storedUsername);
        }
        if (storedUid) {
            setUid(storedUid);
        }
        if (!storedUsername || !storedUid) {
            navigate('/');
        }

        // 'isActive' flag to prevent state updates if the component unmounts during an async operation.
        let isActive = true;
        setLoading(true);

        async function loadInitialData() {
            try {
                // Fetch vehicle and charger data from the backend API concurrently.
                const [vehiclesResp, chargersResp] = await Promise.all([
                    fetch(`${API_BASE_URL}/vehicles`),
                    fetch(`${API_BASE_URL}/chargers`)
                ]);

                // Check if the fetch requests were successful.
                if (!vehiclesResp.ok) throw new Error(`Fetching vehicles failed: ${vehiclesResp.status}`);
                if (!chargersResp.ok) throw new Error(`Fetching chargers failed: ${chargersResp.status}`);

                // Parse the JSON responses.
                const vehiclesData = await vehiclesResp.json();
                const chargersData = await chargersResp.json();

                // If the component is still mounted, update the state with fetched data.
                if (isActive) {
                    setAvailableVehicles(vehiclesData);
                    setAvailableChargers(chargersData);
                    setError(null);
                }
            } catch (err) {
                if (isActive) {
                    setError(err.message);
                    scrollToTop();
                    setAvailableVehicles([]);
                    setAvailableChargers([]);
                }
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        }

        loadInitialData();

        // Cleanup function: runs when the component unmounts.
        return () => {
            isActive = false;
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
    }

    // Adds a new, empty vehicle object to the 'inputVehicles' array.
    const handleAddVehicle = () => {
        setInputVehicles([...inputVehicles, { id: uuidv4(), vehicleModel: '', count: '', countFlex: '' }]);
    };

    // Removes a vehicle object from 'inputVehicles' array based on its 'id'.
    const handleRemoveVehicle = (id) => {
        setInputVehicles(inputVehicles.filter(v => v.id !== id));
    };

    // Updates the specific vehicle object in 'inputVehicles' when an input field changes.
    // It identifies the vehicle by 'id' and updates the property corresponding to 'event.target.name'.
    const handleVehicleInputChange = (id, event) => {
        const { name, value } = event.target;
        setInputVehicles(inputVehicles.map(v => v.id === id ? { ...v, [name]: value } : v));
    };

    // Adds a new, empty charger object to the 'inputChargers' array.
    const handleAddCharger = () => {
        setInputChargers([...inputChargers, { id: uuidv4(), chargerModel: '', count: '', countFlex: '' }]);
    };

    // Removes a charger object from 'inputChargers' array based on its 'id'.
    const handleRemoveCharger = (id) => {
        setInputChargers(inputChargers.filter(c => c.id !== id));
    };

    // Updates the specific charger object in 'inputChargers' when an input field changes.
    const handleChargerInputChange = (id, event) => {
        const { name, value } = event.target;
        setInputChargers(inputChargers.map(c => c.id === id ? { ...c, [name]: value } : c));
    };

    // Adds a new, empty route object to the 'inputRoutes' array.
    const handleAddRoute = () => {
        setInputRoutes([...inputRoutes, { id: uuidv4(), startTime: '', endTime: '', avgSpeed: '', distance: '', numVehicles: '', shiftTime: '' }]);
    };

    // Removes a route object from 'inputRoutes' array based on its 'id'.
    const handleRemoveRoute = (id) => {
        setInputRoutes(inputRoutes.filter(r => r.id !== id));
    };

    // Updates the specific route object in 'inputRoutes' when an input field changes.
    const handleRouteInputChange = (id, event) => {
        const { name, value } = event.target;
        setInputRoutes(inputRoutes.map(r => r.id === id ? { ...r, [name]: value } : r));
    };

    // Updates the 'gridPowerLimit' state when the grid power limit input field changes.
    const handleGridLimitChange = (event) => {
        setGridPowerLimit(event.target.value);
    };

    // Updates the 'peakPrice' state when the peak price input field changes
    const handlePeakPriceChange = (event) => {
        setPeakPrice(event.target.value);
    };

    // Updates a specific hour's energy price in the 'energyPrices' array
    const handleEnergyPriceChange = (hour, event) => {
        const newPrice = event.target.value;
        setEnergyPrices(energyPrices.map(item =>
            item.hour === hour ? { ...item, price: newPrice } : item
        ));
    };

    // Resets energy prices to default mock values
    const resetEnergyPrices = () => {
        setPeakPrice('0.20'); // Reset peak price to default
        setEnergyPrices(Array(24).fill('').map((_, i) => ({
            hour: i,
            // Default mock prices that follow a typical daily pattern
            // Lower at night (hours 0-5, 22-23), higher during peak hours (7-10, 17-20)
            price: i >= 0 && i <= 5 ? '0.08' :
                i >= 6 && i <= 10 ? '0.15' :
                    i >= 11 && i <= 16 ? '0.12' :
                        i >= 17 && i <= 20 ? '0.18' :
                            '0.10' // Hours 21-23
        })));
    };

    async function checkExperimentExists(userId, experimentId) {
        const response = await fetch(`${API_BASE_URL}/users/${userId}/experiments/${experimentId}/exists`);
        const data = await response.json();
        return data.exists;
    }

    const handleSubmit = async (event) => {
        event.preventDefault(); // Prevents the default HTML form submission (which causes a page reload).

        setSubmissionStatus('Submitting data...');
        setError(null);
        const exists = await checkExperimentExists(uid, experimentName);
        if (exists) {
            setError(`Experiment with name "${experimentName}" already exists. Please choose a different name.`);
            setSubmissionStatus('');
            scrollToTop();
            return;
        }

        // Format vehicle data with proper field names for backend
        const formattedVehicles = {
            "TYPE": [],
            "COUNT": [],
            "COUNT FLEX": [],
            "CAPACITY": [],
            "ENERGY CONSUMPTION": [],
            "UNIT COST": [],
            "OPERATING COST": []
        };

        // Validate and provide default values for vehicle properties
        const validateVehicleProperty = (value, propertyName) => {
            const numValue = parseFloat(value);
            if (isNaN(numValue) || numValue <= 0) {
                console.warn(`Invalid ${propertyName}: ${value}, using default value`);
                // Default values for different properties
                const defaults = {
                    'capacity': 200,
                    'energy_consumption': 1,
                    'unit_cost': 500000,
                    'operating_cost': 100,
                    'power': 50,  // Default charger power in kW
                };
                return defaults[propertyName] || 1;
            }
            return numValue;
        };

        // Helper function to ensure counts are positive integers
        const validateCount = (value) => {
            const intValue = parseInt(value, 10);
            if (isNaN(intValue) || intValue <= 0) {
                console.warn(`Invalid count: ${value}, using default value of 1`);
                return 1;
            }
            return intValue;
        };

        // Populate the arrays with data from each vehicle
        inputVehicles.forEach(({ id, vehicleModel, count, countFlex }) => {
            const selectedVehicle = availableVehicles.find(v => v.model === vehicleModel);
            if (selectedVehicle) {
                formattedVehicles["TYPE"].push(vehicleModel);
                formattedVehicles["COUNT"].push(validateCount(count));
                formattedVehicles["COUNT FLEX"].push(parseInt(countFlex, 10) || 0);
                formattedVehicles["CAPACITY"].push(validateVehicleProperty(selectedVehicle.battery_capacity_kwh_max, 'capacity'));
                const range = parseFloat(selectedVehicle.range_km);
                const capacity = parseFloat(selectedVehicle.battery_capacity_kwh_max);
                const energyConsumption = range && range > 0 ? capacity / (range * 0.8) : 0.5; // Default to 0.5 kWh/km if calculation fails
                formattedVehicles["ENERGY CONSUMPTION"].push(validateVehicleProperty(energyConsumption, 'energy_consumption'));
                formattedVehicles["UNIT COST"].push(validateVehicleProperty(selectedVehicle.estimated_cost_usd_max, 'unit_cost'));
                formattedVehicles["OPERATING COST"].push(validateVehicleProperty(selectedVehicle.annual_operating_cost, 'operating_cost') || 0);
            }
        });

        // Format charger data with proper field names for backend
        const formattedChargers = {
            "TYPE": [],
            "COUNT": [],
            "COUNT FLEX": [],
            "POWER": [],
            "UNIT COST": [],
            "OPERATING COST": []
        };

        // Populate the arrays with data from each charger
        inputChargers.forEach(({ id, chargerModel, count, countFlex }) => {
            const selectedCharger = availableChargers.find(c => c.model === chargerModel);
            if (selectedCharger) {
                formattedChargers["TYPE"].push(chargerModel);
                formattedChargers["COUNT"].push(validateCount(count));
                formattedChargers["COUNT FLEX"].push(parseInt(countFlex, 10) || 0);
                let chargerPower = selectedCharger.max_power_output_kw;
                formattedChargers["POWER"].push(validateVehicleProperty(chargerPower, 'power'));
                formattedChargers["UNIT COST"].push(validateVehicleProperty(selectedCharger.estimated_cost_usd_max, 'unit_cost'));
                formattedChargers["OPERATING COST"].push(validateVehicleProperty(selectedCharger.annual_operating_cost, 'operating_cost') || 0);
            }
        });

        // Format route data with proper field names for backend
        const formattedRoutes = {
            "ID": [],
            "START TIME": [],
            "STOP TIME": [],
            "AVERAGE SPEED": [],
            "DISTANCE": [],
            "VEHICLES": [],
            "SHIFT TIME": []
        };

        // Helper function to ensure time is in HH:MM:SS format
        const formatTimeWithSeconds = (timeStr) => {
            if (!timeStr) return "00:00:00";
            // If time is in HH:MM format, add :00 for seconds
            if (timeStr.match(/^\d{2}:\d{2}$/)) {
                return `${timeStr}:00`;
            }
            return timeStr;
        };

        // Populate the arrays with data from each route
        inputRoutes.forEach(({ id, startTime, endTime, avgSpeed, distance, numVehicles, shiftTime }, index) => {
            const formattedStart = formatTimeWithSeconds(startTime);
            const formattedEnd = formatTimeWithSeconds(endTime);


            formattedRoutes["ID"].push(`route_${index + 1}`);
            formattedRoutes["START TIME"].push(formattedStart);
            formattedRoutes["STOP TIME"].push(formattedEnd);
            formattedRoutes["AVERAGE SPEED"].push(parseFloat(avgSpeed) || 25); // Default to 25 km/h if not provided
            formattedRoutes["DISTANCE"].push(parseFloat(distance) || 10); // Default to 10 km if not provided
            formattedRoutes["VEHICLES"].push(Math.max(1, parseInt(numVehicles, 10) || 1)); // Ensure at least 1 vehicle
            formattedRoutes["SHIFT TIME"].push(parseInt(shiftTime, 10) || 30); // Default to 30 minutes shift time
        });

        // Validate experiment name and power data
        if (!experimentName.trim()) {
            setError("Please enter an experiment name");
            setSubmissionStatus('');
            scrollToTop();
            return;
        }

        if (!gridPowerLimit || parseFloat(gridPowerLimit) <= 0) {
            setError("Grid power limit must be greater than zero");
            setSubmissionStatus('');
            scrollToTop();
            return;
        }

        if (!peakPrice || parseFloat(peakPrice) <= 0) {
            setError("Peak price must be greater than zero");
            setSubmissionStatus('');
            scrollToTop();
            return;
        }

        // Check that all hourly prices have values
        const invalidPrices = energyPrices.filter(ep => !ep.price || parseFloat(ep.price) < 0);
        if (invalidPrices.length > 0) {
            setError(`Energy prices must be non-negative for all hours. Please check hour(s): ${invalidPrices.map(ep => ep.hour).join(', ')}`);
            setSubmissionStatus('');
            scrollToTop();
            return;
        }

        // Format the power data for backend
        const formattedPower = {
            "GRID LIMIT": [parseFloat(gridPowerLimit)],
            "PEAK PRICE": [parseFloat(peakPrice)],
            "HOUR": energyPrices.map(ep => ep.hour),
            "ENERGY PRICE": energyPrices.map(ep => parseFloat(ep.price) || 0)
        };

        // Construct data object with proper field names for the backend
        // The backend expects capitalized keys for the main data categories
        const manualInputData = {
            "ExperimentName": experimentName.trim(),
            "Username": username,
            "uid": uid,
            "Vehicles": formattedVehicles,
            "Chargers": formattedChargers,
            "Routes": formattedRoutes,
            "Power": formattedPower
        };
        scrollToTop();
        try {
            const response = await fetch(`${API_BASE_URL}/upload`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(manualInputData)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                // Create a success message with power details
                const powerDetails = result.scenario_data && result.scenario_data.power
                    ? `Grid limit: ${result.scenario_data.power.grid_limit}kW, Peak price: $${result.scenario_data.power.peak_price}/kWh`
                    : '';

                setSubmissionStatus(`Scenario generated successfully! ${powerDetails}`);
                setGeneratedScenario(result.scenario_data);
            } else {
                // Handle specific error messages
                if (result && result['Unfeasible Scenario']) {
                    throw new Error(`Unfeasible Scenario: ${result['Unfeasible Scenario']}`);
                } else if (result && result.error) {
                    throw new Error(result.error);
                } else {
                    throw new Error('Failed to generate scenario - unknown server error');
                }
            }
        } catch (err) {
            console.error('Error submitting data:', err);
            setError(err.message || 'Error connecting to server');
            setSubmissionStatus('Failed to generate scenario');
            scrollToTop();
        }
    };


    if (loading) return <p>Loading available options...</p>;
    if (error && !availableVehicles.length && !availableChargers.length) return <p style={{ color: 'red' }}>Error loading initial data: {error}. Please ensure the backend is running and accessible.</p>;

    return (
        <div className='manual-input-page'>

            {generatedScenario && (
                <div className="scenario-info-container">
                    <h2>Summary of Experiment: {experimentName}</h2>

                    <div className="info-grid">
                        <div className="info-card vehicle-info">
                            <h3>Vehicles</h3>
                            <p><strong>Total:</strong> {generatedScenario.vehicles ?
                                generatedScenario.vehicles.reduce((sum, v) => sum + (v.count || 1), 0) : 0}</p>

                            {generatedScenario.vehicles && generatedScenario.vehicles.length > 0 && (
                                <div className="vehicle-details">
                                    <h4>Vehicle Types</h4>
                                    <div className="table-container">
                                        <table className="summary-table">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Count</th>
                                                    <th>Capacity</th>
                                                    <th>Energy Cons.</th>
                                                    <th>Unit Cost (k$)</th>
                                                    <th>Op. Cost (k$)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {generatedScenario.vehicles.map((vehicle, idx) => (
                                                    <tr key={`vehicle-${idx}`}>
                                                        <td>{vehicle.type || "N/A"}</td>
                                                        <td>{vehicle.count || 1}</td>
                                                        <td>{vehicle.capacity || "N/A"} kWh</td>
                                                        <td>{vehicle.energy_consumption || "N/A"}</td>
                                                        <td>{vehicle.unit_cost ? Number(vehicle.unit_cost).toLocaleString() : "N/A"}</td>
                                                        <td>{vehicle.operating_cost ? Number(vehicle.operating_cost).toLocaleString() : "N/A"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="info-card charger-info">
                            <h3>Chargers</h3>
                            <p><strong>Total:</strong> {generatedScenario.chargers ?
                                generatedScenario.chargers.reduce((sum, c) => sum + (c.count || 1), 0) : 0}</p>

                            {generatedScenario.chargers && generatedScenario.chargers.length > 0 && (
                                <div className="charger-details">
                                    <h4>Charger Types</h4>
                                    <div className="table-container">
                                        <table className="summary-table">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Count</th>
                                                    <th>Power</th>
                                                    <th>Unit Cost (k$)</th>
                                                    <th>Op. Cost (k$)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {generatedScenario.chargers.map((charger, idx) => (
                                                    <tr key={`charger-${idx}`}>
                                                        <td>{charger.type || "N/A"}</td>
                                                        <td>{charger.count || 1}</td>
                                                        <td>{charger.power || "N/A"} kW</td>
                                                        <td>{charger.unit_cost ? Number(charger.unit_cost).toLocaleString() : "N/A"}</td>
                                                        <td>{charger.operating_cost ? Number(charger.operating_cost).toLocaleString() : "N/A"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="info-card route-info">
                            <h3>Routes</h3>
                            <p><strong>Total:</strong> {generatedScenario.routes ? generatedScenario.routes.length : 0}</p>

                            {generatedScenario.routes && generatedScenario.routes.length > 0 && (
                                <div className="route-summary">
                                    <p><strong>Time Range:</strong> {
                                        generatedScenario.routes.length > 0 ?
                                            `${generatedScenario.routes[0].start_time} - 
                                ${generatedScenario.routes[generatedScenario.routes.length - 1].stop_time}` :
                                            "N/A"
                                    }</p>
                                    <p><strong>Total Vehicles Required:</strong> {
                                        generatedScenario.routes.reduce((sum, route) => sum + (route.vehicles || 1), 0)
                                    }</p>

                                    <div className="routes-table-wrapper">
                                        <h4>All Routes</h4>
                                        <div className="table-container">
                                            <table className="summary-table routes-table">
                                                <thead>
                                                    <tr>
                                                        <th>Route ID</th>
                                                        <th>Start Time</th>
                                                        <th>Stop Time</th>
                                                        <th>Average Speed (km/h)</th>
                                                        <th>Distance (km)</th>
                                                        <th>Vehicles</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {generatedScenario.routes.map((route, idx) => (
                                                        <tr key={`route-${idx}`}>
                                                            <td>{route.id || `Route-${idx + 1}`}</td>
                                                            <td>{route.start_time || "N/A"}</td>
                                                            <td>{route.stop_time || "N/A"}</td>
                                                            <td>{route.average_speed || "N/A"}</td>
                                                            <td>{route.distance || "N/A"}</td>
                                                            <td>{route.vehicles || 1}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}


            <div className='manual-input-container'>
                <form onSubmit={handleSubmit}>
                    {error && <p style={{ color: 'red' }}>Error: {error}</p>}
                    {submissionStatus && <p style={{ color: submissionStatus.includes('Failed') ? 'red' : 'green', fontWeight: 'bold' }}>{submissionStatus}</p>}
                    {generatedScenario && (
                        <div className="generated-scenario">
                            <h3>Generated Scenario: {experimentName}</h3>
                            <p>Your scenario has been successfully generated! You can now view the data below.</p>
                            <details>
                                <summary>View Scenario Data</summary>
                                <pre style={{ maxHeight: '300px', overflow: 'auto' }}>
                                    {JSON.stringify(generatedScenario, null, 2)}
                                </pre>
                            </details>
                        </div>
                    )}


                    <h2>Experiment Information</h2>
                    <div className="dynamic-entry">
                        <div className="row">
                            <label htmlFor="experimentName">Experiment Name:</label>
                            <input
                                id="experimentName"
                                type="text"
                                name="experimentName"
                                value={experimentName}
                                onChange={(e) => setExperimentName(e.target.value)}
                                placeholder="Enter a name for this experiment"
                                required
                                style={{ width: '100%', maxWidth: '300px' }}
                            />
                        </div>
                    </div>

                    <hr />

                    <h2>Vehicle Information</h2>
                    {inputVehicles.map((vehicle, index) => (
                        <div key={vehicle.id} className="dynamic-entry">
                            <div className='row'>
                                <label htmlFor={`vehicleModel-${vehicle.id}`}>Vehicle Brand and Model:</label>
                                <select
                                    id={`vehicleModel-${vehicle.id}`}
                                    name="vehicleModel"
                                    value={vehicle.vehicleModel}
                                    onChange={(e) => handleVehicleInputChange(vehicle.id, e)}
                                    required
                                >
                                    <option value="" disabled>Select Vehicle</option>
                                    {availableVehicles.map(v => (
                                        <option key={v.id || v.model} value={v.model}>
                                            {`${v.brand}: ${v.model}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='row'>
                                <label htmlFor={`vehicleCount-${vehicle.id}`}>Count:</label>
                                <input
                                    id={`vehicleCount-${vehicle.id}`}
                                    type="number"
                                    min="0"
                                    name="count"
                                    value={vehicle.count}
                                    onChange={(e) => handleVehicleInputChange(vehicle.id, e)}
                                    placeholder="e.g., 10"
                                    required
                                />
                            </div>
                            <div className='row'>
                                <label htmlFor={`vehicleCountFlex-${vehicle.id}`}>Count Flex:</label>
                                <input
                                    id={`vehicleCountFlex-${vehicle.id}`}
                                    type="number"
                                    min="0"
                                    name="countFlex"
                                    value={vehicle.countFlex}
                                    onChange={(e) => handleVehicleInputChange(vehicle.id, e)}
                                    placeholder="e.g., 2"
                                    required
                                />
                            </div>
                            {inputVehicles.length > 1 && (
                                <button type="button" onClick={() => handleRemoveVehicle(vehicle.id)} className="remove-btn">Remove Vehicle</button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={handleAddVehicle} className="add-btn">Add Another Vehicle</button>

                    <hr />
                    <h2>Charger Information</h2>
                    {inputChargers.map((charger, index) => (
                        <div key={charger.id} className="dynamic-entry">
                            <div className='row'>
                                <label htmlFor={`chargerModel-${charger.id}`}>Charger Brand and Model:</label>
                                <select
                                    id={`chargerModel-${charger.id}`}
                                    name="chargerModel"
                                    value={charger.chargerModel}
                                    onChange={(e) => handleChargerInputChange(charger.id, e)}
                                    required
                                >
                                    <option value="" disabled>Select Charger</option>
                                    {availableChargers.map(c => {
                                        // Calculate a reasonable power value to display
                                        let powerDisplay = c.power_kw;
                                        if (!powerDisplay && c.output_voltage_v_dc_max && c.output_current_a_max) {
                                            powerDisplay = (c.output_voltage_v_dc_max * c.output_current_a_max / 1000).toFixed(1);
                                        } else if (!powerDisplay) {
                                            powerDisplay = "50"; // Default
                                        }
                                        return (
                                            <option key={c.id || c.model} value={c.model}>
                                                {`${c.brand}: ${c.model} (${powerDisplay}kW)`}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className='row'>
                                <label htmlFor={`chargerCount-${charger.id}`}>Count:</label>
                                <input
                                    id={`chargerCount-${charger.id}`}
                                    type="number"
                                    min="0"
                                    name="count"
                                    value={charger.count}
                                    onChange={(e) => handleChargerInputChange(charger.id, e)}
                                    placeholder="e.g., 5"
                                    required
                                />
                            </div>
                            <div className='row'>
                                <label htmlFor={`chargerCountFlex-${charger.id}`}>Count Flex:</label>
                                <input
                                    id={`chargerCountFlex-${charger.id}`}
                                    type="number"
                                    min="0"
                                    name="countFlex"
                                    value={charger.countFlex}
                                    onChange={(e) => handleChargerInputChange(charger.id, e)}
                                    placeholder="e.g., 1"
                                    required
                                />
                            </div>
                            {inputChargers.length > 1 && (
                                <button type="button" onClick={() => handleRemoveCharger(charger.id)} className="remove-btn">Remove Charger</button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={handleAddCharger} className="add-btn">Add Another Charger</button>

                    <hr />
                    <h2>Route Information</h2>
                    {inputRoutes.map((route, index) => (
                        <div key={route.id} className="dynamic-entry route-entry">
                            <div className='row'>
                                <label htmlFor={`startTime-${route.id}`}>Start Time:</label>
                                <input
                                    id={`startTime-${route.id}`}
                                    type="time"
                                    name="startTime"
                                    value={route.startTime}
                                    onChange={(e) => handleRouteInputChange(route.id, e)}
                                    required
                                />
                            </div>
                            <div className='row'>
                                <label htmlFor={`endTime-${route.id}`}>End Time:</label>
                                <input
                                    id={`endTime-${route.id}`}
                                    type="time"
                                    name="endTime"
                                    value={route.endTime}
                                    onChange={(e) => handleRouteInputChange(route.id, e)}
                                    required
                                />
                            </div>
                            <div className='row'>
                                <label htmlFor={`avgSpeed-${route.id}`}>Average Speed (km/h):</label>
                                <input
                                    id={`avgSpeed-${route.id}`}
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    name="avgSpeed"
                                    value={route.avgSpeed}
                                    onChange={(e) => handleRouteInputChange(route.id, e)}
                                    placeholder="e.g., 50"
                                    required
                                />
                            </div>
                            <div className='row'>
                                <label htmlFor={`distance-${route.id}`}>Distance (km):</label>
                                <input
                                    id={`distance-${route.id}`}
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    name="distance"
                                    value={route.distance}
                                    onChange={(e) => handleRouteInputChange(route.id, e)}
                                    placeholder="e.g., 100"
                                    required
                                />
                            </div>
                            <div className='row'>
                                <label htmlFor={`numVehicles-${route.id}`}>Number of Vehicles on Route:</label>
                                <input
                                    id={`numVehicles-${route.id}`}
                                    type="number"
                                    min="1"
                                    name="numVehicles"
                                    value={route.numVehicles}
                                    onChange={(e) => handleRouteInputChange(route.id, e)}
                                    placeholder="e.g., 3"
                                    required
                                />
                            </div>
                            <div className='row'>
                                <label htmlFor={`shiftTime-${route.id}`}>Shift Time (minutes):</label>
                                <input
                                    id={`shiftTime-${route.id}`}
                                    type="number"
                                    min="0"
                                    name="shiftTime"
                                    value={route.shiftTime}
                                    onChange={(e) => handleRouteInputChange(route.id, e)}
                                    placeholder="e.g., 30"
                                    required
                                />
                            </div>
                            {inputRoutes.length > 1 && (
                                <button type="button" onClick={() => handleRemoveRoute(route.id)} className="remove-btn">Remove Route</button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={handleAddRoute} className="add-btn">Add Another Route</button>

                    <hr />
                    <h2>Grid Information</h2>
                    <div className='row'>
                        <label htmlFor="gridPowerLimit">Grid Power Limit (kW):</label>
                        <input
                            id="gridPowerLimit"
                            type="number"
                            min="0"
                            name="gridPowerLimit"
                            value={gridPowerLimit}
                            onChange={handleGridLimitChange}
                            placeholder="e.g., 150"
                            required
                        />
                    </div>
                    <p className="hint-text" style={{ marginTop: '5px' }}>
                        Note: The total power of all chargers (power × count) must be less than or equal to the grid power limit.
                    </p>

                    <div className='row'>
                        <label htmlFor="peakPrice">Peak Price ($/kWh):</label>
                        <input
                            id="peakPrice"
                            type="number"
                            step="0.01"
                            min="0"
                            name="peakPrice"
                            value={peakPrice}
                            onChange={handlePeakPriceChange}
                            placeholder="e.g., 0.15"
                            required
                        />
                    </div>

                    <h3>Hourly Energy Prices</h3>
                    <p>Set the energy price for each hour of the day (0-23):</p>
                    <div className="energy-price-controls">
                        <button type="button" onClick={resetEnergyPrices} className="reset-btn">Reset to Default Values</button>

                    </div>
                    <p className="hint-text">Energy prices follow a time-of-day pattern with lower prices at night (hours 0-5, 21-23) and higher during peak hours (7-10, 17-20).</p>
                    <div className="energy-price-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Hour</th>
                                    <th>Energy Price ($/kWh)</th>
                                    <th>Hour</th>
                                    <th>Energy Price ($/kWh)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...Array(12)].map((_, i) => (
                                    <tr key={i}>
                                        <td>{i}</td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={energyPrices[i].price}
                                                onChange={(e) => handleEnergyPriceChange(i, e)}
                                                placeholder="e.g., 0.10"
                                                required
                                            />
                                        </td>
                                        <td>{i + 12}</td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={energyPrices[i + 12].price}
                                                onChange={(e) => handleEnergyPriceChange(i + 12, e)}
                                                placeholder="e.g., 0.12"
                                                required
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <br /><br />
                    <div className="submit-container">
                        <button type="submit" className="submit">Generate Scenario</button>
                    </div>
                </form>
            </div>
        </div>

    );
}